import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { fetchAllPages } from "../api/client";
import { assignmentState, type TaskAssignments } from "../lib/team";
import {
  assignLteTask,
  createLteTask,
  declareLteException,
  listLteAssignments,
  listLteTasks,
  listSupervisorOpen,
  sendLteCommand,
  submitLteProof,
  LTE_ACTIVE_ASSIGNMENT_STATUSES,
  type CreateLteTaskInput,
  type LteAssignment,
  type LteCommand,
  type LteTask,
} from "../api/lte";

export function useLteTasks() {
  return useQuery({
    queryKey: ["lte", "tasks"],
    queryFn: () => fetchAllPages((cursor) => listLteTasks(cursor)),
    staleTime: 30_000,
  });
}

export function useSupervisorOpen(enabled = true) {
  return useQuery({
    queryKey: ["lte", "supervisor", "open"],
    queryFn: listSupervisorOpen,
    staleTime: 30_000,
    enabled,
  });
}

/** Assignations LTE par tâche. GET /lte/tasks ne filtre pas par assigné et la
 * tâche ne porte aucune colonne titulaire : rattacher une tâche à un
 * collaborateur impose une requête par tâche (cachée 60s), bornée aux tâches
 * ouvertes. Ne retient que les statuts actifs — une assignation declined ou
 * cancelled libère le slot côté domaine. */
export function useLteAssignmentsBatch(
  taskIds: string[],
): Record<string, TaskAssignments<LteAssignment>> {
  const results = useQueries({
    queries: taskIds.map((id) => ({
      queryKey: ["lte", "assignments", id],
      queryFn: () => listLteAssignments(id),
      staleTime: 60_000,
    })),
  });

  const out: Record<string, TaskAssignments<LteAssignment>> = {};
  results.forEach((r, i) => {
    const id = taskIds[i];
    if (!id) return;
    out[id] = {
      items: (r.data?.items ?? []).filter((a) =>
        LTE_ACTIVE_ASSIGNMENT_STATUSES.includes(a.status),
      ),
      state: assignmentState(r),
    };
  });
  return out;
}

function useInvalidateLte() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["lte"] });
}

export function useLteCommand() {
  const qc = useQueryClient();
  const invalidate = useInvalidateLte();
  return useMutation({
    mutationFn: (args: {
      id: string;
      command: LteCommand;
      version: number;
      reason?: string;
    }) => sendLteCommand(args.id, args.command, args.version, args.reason),
    onSuccess: (updated) => {
      // La sheet Terrain lit la tâche depuis ce cache : écrire la réponse serveur
      // immédiatement, sinon un second tap avant la fin du refetch repartirait
      // avec l'ancienne version → 409 « task was modified concurrently ».
      qc.setQueryData<{ items: LteTask[] }>(["lte", "tasks"], (old) =>
        old
          ? {
              ...old,
              items: old.items.map((t) => (t.id === updated.id ? updated : t)),
            }
          : old,
      );
      invalidate();
    },
  });
}

export function useSubmitLteProof() {
  const invalidate = useInvalidateLte();
  return useMutation({
    mutationFn: (args: {
      id: string;
      proof_type: string;
      proof_uri?: string;
    }) =>
      submitLteProof(args.id, {
        proof_type: args.proof_type,
        proof_uri: args.proof_uri,
      }),
    onSuccess: invalidate,
  });
}

export function useDeclareLteException() {
  const invalidate = useInvalidateLte();
  return useMutation({
    mutationFn: (args: { id: string; kind: string; severity: string }) =>
      declareLteException(args.id, {
        kind: args.kind,
        severity: args.severity,
      }),
    onSuccess: invalidate,
  });
}

export function useCreateLteTask() {
  const invalidate = useInvalidateLte();
  return useMutation({
    mutationFn: (body: CreateLteTaskInput) => createLteTask(body),
    onSuccess: invalidate,
  });
}

export function useAssignLteTask() {
  const invalidate = useInvalidateLte();
  return useMutation({
    mutationFn: (args: {
      id: string;
      target_type: string;
      role: string;
      target_ref: string;
    }) =>
      assignLteTask(args.id, {
        target_type: args.target_type,
        role: args.role,
        target_ref: args.target_ref,
      }),
    onSuccess: invalidate,
  });
}
